import { useCallback, useEffect, useRef, useState } from 'react';
import { beatIndexAt } from './timeline';

// A beat waits for its narration at most this long before moving on
const MAX_HOLD_MS = 8000;
// Re-render the picture at ~30 fps; the clock itself runs every frame
const PAINT_MS = 33;

/**
 * Video clock: play / pause / seek / speed over the timeline. While `holdRef`
 * names the current beat (its narration is still speaking) the clock stops at
 * the beat's end instead of cutting the sentence off.
 */
export const usePlaybackClock = (timeline) => {
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const timeRef = useRef(0);
  const holdRef = useRef({ index: -1, since: 0 });

  const seek = useCallback((value) => {
    const next = Math.min(timeline.total, Math.max(0, value));
    timeRef.current = next;
    holdRef.current = { index: -1, since: 0 };
    setTime(next);
  }, [timeline.total]);

  useEffect(() => {
    if (!playing) return undefined;
    let frame;
    let last = performance.now();
    let lastPaint = 0;
    const tick = (now) => {
      // The first frame's timestamp can predate `last`: never run backwards
      const delta = Math.max(0, Math.min(100, now - last)) * rate;
      last = now;
      let next = timeRef.current + delta;
      const index = beatIndexAt(timeline.beats, timeRef.current);
      const beat = timeline.beats[index];
      const end = beat.start + beat.duration;
      const hold = holdRef.current;
      if (hold.index === index && next >= end - 1) {
        if (!hold.since) hold.since = now;
        if (now - hold.since < MAX_HOLD_MS) next = end - 1;
        else holdRef.current = { index: -1, since: 0 };
      }
      if (next >= timeline.total) {
        timeRef.current = timeline.total;
        setTime(timeline.total);
        setPlaying(false);
        return;
      }
      timeRef.current = next;
      if (now - lastPaint >= PAINT_MS) {
        lastPaint = now;
        setTime(next);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, rate, timeline]);

  return { time, playing, setPlaying, rate, setRate, seek, holdRef };
};

/**
 * Voice-over with the browser's speech synthesis, in the UI language. Not
 * every browser has a Turkish voice; `available` says whether one was found.
 */
export const useNarration = (language) => {
  const synth = typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null;
  const [voice, setVoice] = useState(null);

  useEffect(() => {
    if (!synth) return undefined;
    const pickVoice = () => {
      const prefix = language === 'tr' ? 'tr' : 'en';
      const matches = synth.getVoices().filter((item) => (item.lang || '').toLowerCase().startsWith(prefix));
      setVoice(matches.find((item) => /natural|online|neural|google/i.test(item.name)) || matches.find((item) => item.localService) || matches[0] || null);
    };
    pickVoice();
    synth.addEventListener?.('voiceschanged', pickVoice);
    return () => synth.removeEventListener?.('voiceschanged', pickVoice);
  }, [synth, language]);

  const cancel = useCallback(() => {
    synth?.cancel();
  }, [synth]);

  const speak = useCallback((text, rate, onEnd) => {
    if (!synth || !voice) {
      onEnd?.();
      return;
    }
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.rate = rate;
    utterance.onend = () => onEnd?.();
    utterance.onerror = () => onEnd?.();
    synth.speak(utterance);
  }, [synth, voice]);

  useEffect(() => cancel, [cancel]);

  return { available: Boolean(voice), speak, cancel };
};

/** "3:07" */
export const formatClock = (ms) => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};
